import { Trans } from '@lingui/react/macro';
import {
  Avatar,
  AvatarFallback,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/videa-components/src/index';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';

import { useGetDateFNSLocale } from '../../../../locales/helpers';
import type { Appointment } from '../../../../react-query/useAppointments';
import { parseAndFormatDate } from '../../../../utils/parseDate';

import { getPatientAge } from './patientInfoSheet/PatientInfo';

export const AppointmentHeaderContent = ({
  appointment,
  hideProviderAvatar = false,
  isDrawer,
}: {
  appointment: Appointment;
  hideProviderAvatar?: boolean;
  isDrawer?: boolean;
}) => {
  const dateFNSLocale = useGetDateFNSLocale();
  const privacyOn = useSelector((state: any) => state.login?.user?.privacyOn);

  if (!appointment) {
    return null;
  }

  const provider = appointment.appointment.provider;
  const operatory = appointment.appointment.operatory;
  const startDate = appointment.appointment.start;
  const patientDOB = appointment.patient?.dob;

  const formattedDate = (() => {
    const appointmentDate = new Date(startDate);
    const today = new Date();
    const isToday = appointmentDate.toDateString() === today.toDateString();

    return isToday
      ? format(appointmentDate, 'h:mmaaa', {
          locale: dateFNSLocale,
        })
      : format(appointmentDate, 'MMM d, yyyy h:mmaaa', {
          locale: dateFNSLocale,
        });
  })();

  const age = getPatientAge(patientDOB);

  const formattedDOB = parseAndFormatDate(patientDOB, undefined, dateFNSLocale);

  const renderAvatar = () => {
    if (!provider?.firstName || !provider?.lastName) {
      return null;
    }
    const initials = `${provider.firstName[0]}${provider.lastName[0]}`;

    return (
      <Avatar className="vai-bg-slate-50 vai-p-0 vai-m-0 vai-border-none vai-text-white vai-font-bold vai-text-sm vai-h-7 vai-w-7 ">
        <AvatarFallback className="vai-bg-text-accent vai-p-0 vai-m-0 vai-border-none vai-text-white vai-font-bold vai-text-sm vai-h-7 vai-w-7">
          {initials}
        </AvatarFallback>
      </Avatar>
    );
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center vai-mt-0 vai-mb-2">
          <span
            className="vai-text-sm vai-font-thin"
            data-testid="patient-age-and-dob"
          >
            {patientDOB && startDate && (
              <span>
                {privacyOn ? (
                  <Trans>
                    Age {age} | DOB: {formattedDOB}
                  </Trans>
                ) : (
                  <span className="vai-text-gray-300 vai-blur-[5px] vai-select-none vai-font-mono vai-text-sm vai-font-thin">
                    &#x2588;&#x2588;&#x2588;&#x2588;&#x2588;&#x2588;&#x2588;&#x2588;&#x2588;&#x2588;&#x2588;&#x2588;
                  </span>
                )}
              </span>
            )}
          </span>
        </div>
        {!isDrawer && (
          <div className="vai-flex vai-flex-row vai-flex-wrap vai-gap-2 vai-items-center">
            {provider && provider.firstName && provider.lastName && (
              <Tooltip delayDuration={100}>
                {!hideProviderAvatar && (
                  <TooltipTrigger className="vai-bg-slate-50 vai-p-0 vai-m-0 vai-border-none">
                    {renderAvatar()}
                  </TooltipTrigger>
                )}
                <TooltipContent>
                  {`${provider.firstName} ${provider.lastName}`}
                </TooltipContent>
              </Tooltip>
            )}
            {operatory?.name && (
              <span
                className="vai-text-lg vai-font-bold"
                data-text={operatory.name}
              >
                {operatory.name}
              </span>
            )}
            {startDate && (
              <div className="flex items-center">
                <span className="vai-text-md vai-font-thin">
                  {formattedDate}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};
